import React, { useState, useEffect } from "react";
import "../styles/GameHistoryModal.css";
import { FaGamepad, FaPlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { usePayment } from "../context/PaymentContext";
import { useTranslation } from "react-i18next";

import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from "../utils/numberFormat";
const API_URL = getApiUrl();

// Функция для получения символа валюты (как в Header.js)
const getCurrencySymbol = (currency) => {
    switch (currency) {
        case 'USD': return '$';
        case 'RUB': return '₽';
        case 'BRL': return 'R$';
        case 'EUR': return '€';
        default: return '';
    }
};

const GameHistoryModal = ({ onClose }) => {
    const [sessions, setSessions] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedSessions, setExpandedSessions] = useState(new Set());
    const [sessionTransactions, setSessionTransactions] = useState({});
    const [loadingTransactions, setLoadingTransactions] = useState(new Set());
    const navigate = useNavigate();
    const { profile } = usePayment();

    useEffect(() => {
        fetchData();
    }, [profile?.timezone]);

    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            // Получаем сессии с информацией об играх (уже включает данные об играх из JOIN)
            const sessionsRes = await fetch(`${API_URL}/api/games/sessions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ type: 'history' })
            });
            const sessionsData = await sessionsRes.json();
            if (!sessionsData.success) throw new Error(sessionsData.message || "Ошибка загрузки истории игр");

            setSessions(sessionsData.sessions);

            console.log('📊 GameHistoryModal: Загружено сессий:', {
                total: sessionsData.sessions?.length || 0,
                sessions_with_game_info: sessionsData.sessions?.filter(s => s.game_name).length || 0,
                sample_session: sessionsData.sessions?.[0] ? {
                    game_id: sessionsData.sessions[0].game_id,
                    game_name: sessionsData.sessions[0].game_name,
                    has_image: !!sessionsData.sessions[0].game_image
                } : null
            });

            // Больше не нужно загружать все игры - информация уже есть в сессиях
            setGames([]);
        } catch (e) {
            setError(e.message || "Ошибка загрузки истории игр");
        } finally {
            setLoading(false);
        }
    };

    // Функция для загрузки транзакций сессии
    const fetchSessionTransactions = async (sessionId) => {
        if (sessionTransactions[sessionId] || loadingTransactions.has(sessionId)) {
            return; // Уже загружены или загружаются
        }

        setLoadingTransactions(prev => new Set(prev.add(sessionId)));

        try {
            console.log('📊 Загрузка транзакций для сессии:', sessionId);

            const response = await fetch(`${API_URL}/api/sessions/${sessionId}/transactions`, {
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                setSessionTransactions(prev => ({
                    ...prev,
                    [sessionId]: data.transactions
                }));

                console.log('📊 Загружено транзакций:', {
                    sessionId,
                    count: data.transactions.length,
                    transactions: data.transactions
                });
            } else {
                console.error('Ошибка загрузки транзакций:', data.message);
            }
        } catch (error) {
            console.error('Ошибка загрузки транзакций сессии:', error);
        } finally {
            setLoadingTransactions(prev => {
                const newSet = new Set(prev);
                newSet.delete(sessionId);
                return newSet;
            });
        }
    };

    // Функция для переключения раскрытия сессии
    const toggleSessionExpansion = async (sessionId) => {
        const newExpandedSessions = new Set(expandedSessions);

        if (expandedSessions.has(sessionId)) {
            // Скрываем сессию
            newExpandedSessions.delete(sessionId);
        } else {
            // Раскрываем сессию
            newExpandedSessions.add(sessionId);
            // Загружаем транзакции если их еще нет
            await fetchSessionTransactions(sessionId);
        }

        setExpandedSessions(newExpandedSessions);
    };

    // Сортировка по дате создания (новые сверху)
    const sortedSessions = [...sessions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Вспомогательные функции - теперь информация об игре уже есть в сессии
    const getGameInfo = (session) => {
        return {
            uuid: session.game_id,
            name: session.game_name || session.game_id,
            image: session.game_image || '/assets/game-placeholder.png',
            provider: session.game_provider
        };
    };

    // Функция для конвертации времени из UTC+3 в локальное время пользователя
    const convertTimeToUserTimezone = (date) => {
        if (!date) return null;
        const d = new Date(date);
        // Вычитаем 3 часа, так как время в БД в UTC+3
        d.setHours(d.getHours() - 3);
        return d;
    };

    // Функция для получения смещения часового пояса пользователя
    const getUserTimezoneOffset = () => {
        if (!profile?.timezone) return '';
        const offset = new Date().toLocaleString('ru-RU', {
            timeZone: profile.timezone,
            timeZoneName: 'shortOffset'
        }).split(' ').pop();
        return offset.replace('GMT', 'UTC');
    };

    // Обновленная функция форматирования времени
    const formatTime = (date) => {
        if (!date) return '-';
        const d = convertTimeToUserTimezone(date);
        if (!d) return '-';
        if (!profile?.timezone) return d.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        return d.toLocaleString('ru-RU', {
            timeZone: profile.timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    // Функция форматирования валюты
    const formatCurrency = (val, currency) => (val !== undefined && val !== null ? formatBalanceLocale(val) : '-') + (currency ? ` ${getCurrencySymbol(currency)}` : '');

    // Функции для отображения транзакций
    const getTransactionTypeLabel = (action) => {
        switch (action) {
            case 'bet': return 'Ставка';
            case 'win': return 'Выигрыш';
            case 'refund': return 'Возврат';
            default: return action;
        }
    };

    const formatTransactionTime = (date) => {
        if (!date) return '-';
        const d = convertTimeToUserTimezone(date);
        if (!d) return '-';
        if (!profile?.timezone) return d.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        return d.toLocaleString('ru-RU', {
            timeZone: profile.timezone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    // Обновленная функция форматирования даты
    const formatDate = (date) => {
        if (!date) return '-';
        const d = convertTimeToUserTimezone(date);
        if (!d) return '-';
        if (!profile?.timezone) return d.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });

        return d.toLocaleString('ru-RU', {
            timeZone: profile.timezone,
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const { t } = useTranslation("");

    return (
        <div className="profile-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <div className="profile-modal-header">
                    <h3 className="profile-modal-title">{t("GameHistory.modalTitle")}</h3>
                    <button className="profile-modal-close" onClick={onClose} aria-label={t("GameHistory.close")}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="12" fill="#23232e" />
                            <path d="M16 8L8 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                            <path d="M8 8L16 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                {loading ? (
                    <div className="game-history-loading">{t("GameHistory.loading")}</div>
                ) : error ? (
                    <div className="game-history-error">{error}</div>
                ) : (
                    <div className="game-history-list">
                        {sortedSessions.length === 0 ? (
                            <div className="game-history-empty">{t("GameHistory.empty")}</div>
                        ) : (
                            sortedSessions.map((session, idx) => {
                                const game = getGameInfo(session);
                                const banner = game.image;
                                const name = game.name;
                                const timeStart = formatTime(session.created_at);
                                const timeEnd = formatTime(session.last_transaction_at);
                                const totalBet = formatCurrency(session.total_bet, session.currency);
                                const totalWin = formatCurrency(session.total_win, session.currency);
                                const currentDate = formatDate(session.created_at);
                                const prevDate = idx > 0 ? formatDate(sortedSessions[idx - 1].created_at) : null;
                                const showDate = idx === 0 || currentDate !== prevDate;

                                const handlePlay = (e) => {
                                    e.stopPropagation();
                                    if (game.uuid) {
                                        onClose();
                                        navigate(`/gis-game/${game.uuid}?name=${encodeURIComponent(game.name)}`);
                                    }
                                };

                                const sessionId = session.gis_session_id || session.session_id;

                                const handleSessionClick = () => {
                                    toggleSessionExpansion(sessionId);
                                };

                                const isExpanded = expandedSessions.has(sessionId);
                                const transactions = sessionTransactions[sessionId] || [];
                                const isLoadingTx = loadingTransactions.has(sessionId);

                                return (
                                    <React.Fragment key={sessionId}>
                                        {showDate && (
                                            <div className="game-history-date-separator">{currentDate}</div>
                                        )}
                                        <div className={`game-history-session-wrapper${isExpanded ? ' expanded' : ''}`}>
                                            <div className="game-history-list-row" onClick={handleSessionClick}>
                                                <img src={banner} alt={name} className="game-history-banner" onError={e => { e.target.src = '/assets/game-placeholder.png'; }} />
                                                <div className="game-history-main-info">
                                                    <div className="game-history-row-flex">
                                                        <span className="game-history-title">{name}</span>
                                                        <span className="game-history-bet-inline">{t("GameHistory.bets")}: {totalBet}</span>
                                                    </div>
                                                    <div className="game-history-row-flex">
                                                        <span className="game-history-time">{timeStart} - {timeEnd}</span>
                                                        <span className="game-history-win-inline">{t("GameHistory.wins")}: {totalWin}</span>
                                                    </div>
                                                </div>
                                                <div className="game-history-play-btn" onClick={handlePlay} title={t("GameHistory.play")}><FaPlay /></div>
                                            </div>

                                            {isExpanded && (
                                                <div className="session-transactions">
                                                    <div className="session-transactions-header">
                                                        {isLoadingTx ? 'Загрузка транзакций...' : `Транзакции (${transactions.length})`}
                                                    </div>
                                                    {!isLoadingTx && (
                                                        <div className="session-transactions-list">
                                                            {transactions.length === 0 ? (
                                                                <div style={{ textAlign: 'center', color: '#b6b6c6', padding: '10px' }}>
                                                                    Транзакций не найдено
                                                                </div>
                                                            ) : (
                                                                transactions.map((round, roundIdx) => (
                                                                    <div key={roundIdx} className="session-round-item">
                                                                        <div className="session-round-header">
                                                                            <span className="session-round-title">
                                                                                Раунд {roundIdx + 1}
                                                                                {round.is_freespin && (
                                                                                    <span className="freespin-badge">FREESPIN</span>
                                                                                )}
                                                                            </span>
                                                                            <span className="session-round-time">
                                                                                {formatTransactionTime(round.created_at)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="session-round-details">
                                                                            {round.bet_amount > 0 && (
                                                                                <div className="session-transaction-item">
                                                                                    <span className="session-transaction-type">Ставка</span>
                                                                                    <span className="session-transaction-amount bet">
                                                                                        -{formatBalanceLocale(round.bet_amount)} {getCurrencySymbol(round.currency)}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            {round.win_amount > 0 && (
                                                                                <div className="session-transaction-item">
                                                                                    <span className="session-transaction-type">Выигрыш</span>
                                                                                    <span className="session-transaction-amount win">
                                                                                        +{formatBalanceLocale(round.win_amount)} {getCurrencySymbol(round.currency)}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            {round.bet_amount === 0 && round.win_amount === 0 && (
                                                                                <div className="session-transaction-item">
                                                                                    <span className="session-transaction-type">Нет данных</span>
                                                                                    <span className="session-transaction-amount">
                                                                                        0.00 {getCurrencySymbol(round.currency)}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GameHistoryModal; 